import { useAppData } from "../../../lib/AppDataContext";
import { PROPERTY_TYPES, PROPERTY_STATUSES } from "../../../lib/seed";
import { highlightsFor, HL_FIELDS, HAS_LISTING, LISTING_OPTIONS } from "../../../lib/propertyTypeSchema";
import { SearchSelect } from "../../../components/shared/SearchSelect";

/* Step 1 — Basic Info. Everything required to publish lives in this step
   plus Step 2 (Configs). Also collects the About heading/body and the
   project-highlights grid that renders on the customer project page
   (Land Parcel, Towers, Floors, Total Units, Parking, Total Amenities,
   Vastu, RERA Possession, Litigation). RERA No. = RERA ID. */
export function BasicInfoStep({ data, setData, errors }) {
  const { builders, localities, cities } = useAppData();
  const set = (k, v) => setData({ ...data, [k]: v });

  const localityChoices = data.city
    ? localities.filter((l) => l.cityId === data.city || l.city?.name === data.city || l.city?.id === data.city)
    : localities;

  const fieldCls = "w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500";
  const labelCls = "text-xs font-medium text-slate-600 mb-1.5 block";
  const errorCls = "text-[11px] text-red-600 mt-1";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelCls}>Project title <span className="text-red-600">*</span></label>
          <input value={data.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Godrej Hill Retreat" className={fieldCls} autoFocus />
          {errors.title && <p className={errorCls}>{errors.title}</p>}
        </div>

        <div>
          <label className={labelCls}>Builder <span className="text-red-600">*</span></label>
          <SearchSelect value={data.builderId} onChange={(id) => set("builderId", id)} options={builders} placeholder="Search builder…" />
          {errors.builderId && <p className={errorCls}>{errors.builderId}</p>}
        </div>

        <div>
          <label className={labelCls}>Property type <span className="text-red-600">*</span></label>
          <SearchSelect value={data.propertyType} onChange={(v) => set("propertyType", v)} options={PROPERTY_TYPES.map((t) => ({ id: t, name: t }))} placeholder="Select property type…" />
        </div>

        <div>
          <label className={labelCls}>City <span className="text-red-600">*</span></label>
          <SearchSelect value={data.city} onChange={(v) => set("city", v)} options={cities.map((c) => ({ id: c.name, name: c.name }))} placeholder="Select city…" />
          {errors.city && <p className={errorCls}>{errors.city}</p>}
        </div>

        <div>
          <label className={labelCls}>Locality <span className="text-red-600">*</span></label>
          <SearchSelect value={data.localityId} onChange={(id) => set("localityId", id)} options={localityChoices} disabled={!data.city} placeholder={data.city ? "Search locality…" : "Pick a city first"} />
          {errors.localityId && <p className={errorCls}>{errors.localityId}</p>}
        </div>

        <div>
          <label className={labelCls}>Status</label>
          <SearchSelect value={data.status} onChange={(v) => set("status", v)} options={PROPERTY_STATUSES.map((s) => ({ id: s, name: s.charAt(0).toUpperCase() + s.slice(1) }))} placeholder="Select status…" />
          <p className="text-[10px] text-slate-400 mt-1">Draft = hidden. Published = live. Archived = hidden but retained.</p>
        </div>

        <div>
          <label className={labelCls}>Possession Status <span className="text-slate-400 font-normal">(customer badge)</span></label>
          <SearchSelect value={data.possessionStatus || "under_construction"} onChange={(v) => set("possessionStatus", v)} options={[
            { id: "under_construction", name: "Under Construction" },
            { id: "nearing_possession", name: "Nearing Possession" },
            { id: "ready_to_move", name: "Ready to Move" },
            { id: "new_launch", name: "New Launch" },
          ]} placeholder="Select possession status…" />
        </div>

        <div className="sm:col-span-2 flex flex-wrap gap-5 pt-1">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!data.reraVerified} onChange={(e) => set("reraVerified", e.target.checked)} className="w-4 h-4" />
            Show <span className="font-medium">RERA Verified</span> badge
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={!!data.zeroBrokerage} onChange={(e) => set("zeroBrokerage", e.target.checked)} className="w-4 h-4" />
            Show <span className="font-medium">Zero Brokerage</span> badge
          </label>
        </div>

        <div>
          <label className={labelCls}>RERA ID <span className="text-slate-400 font-normal">(shows as RERA No.)</span></label>
          <input value={data.reraId} onChange={(e) => set("reraId", e.target.value.toUpperCase())} placeholder="P52100049832" className={fieldCls + " uppercase tracking-wider"} />
        </div>

        <div>
          <label className={labelCls}>Possession date</label>
          <input type="date" value={data.possessionDate} onChange={(e) => set("possessionDate", e.target.value)} className={fieldCls + " tabular-nums"} />
        </div>

        <div className="sm:col-span-2">
          <label className={labelCls}>Short description</label>
          <input value={data.shortDescription} onChange={(e) => set("shortDescription", e.target.value)} placeholder="One-line description shown on listing cards" className={fieldCls} />
          <p className="text-[10px] text-slate-400 mt-1">{data.shortDescription.length} / 160 characters &mdash; appears as the card subtitle.</p>
        </div>
      </div>

      {/* About — heading + body, mirrors the public project page */}
      <div className="pt-5 border-t border-slate-100">
        <h4 className="text-sm font-medium text-slate-700 mb-3">About this project</h4>
        <div className="space-y-3">
          <div>
            <label className={labelCls}>About heading</label>
            <input value={data.aboutHeading || ""} onChange={(e) => set("aboutHeading", e.target.value)} placeholder="e.g. A grounded place to come home to." className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>About description</label>
            <textarea value={data.fullDescription} onChange={(e) => set("fullDescription", e.target.value)} rows={5}
              placeholder="Write the about paragraph as it appears on the project page…" className={fieldCls + " resize-y"} />
          </div>
        </div>
      </div>

      {/* Listing (Buy/Lease) — Plot only */}
      {HAS_LISTING[data.propertyType] && (
        <div className="pt-5 border-t border-slate-100">
          <div className="max-w-xs">
            <label className={labelCls}>Listing <span className="text-red-600">*</span></label>
            <SearchSelect value={data.listing || ""} onChange={(v) => set("listing", v)} options={LISTING_OPTIONS.map((o) => ({ id: o, name: o }))} placeholder="Select…" />
          </div>
        </div>
      )}

      {/* Project highlights — type-reactive stat grid (all required) */}
      <div className="pt-5 border-t border-slate-100">
        <h4 className="text-sm font-medium text-slate-700 mb-1">Project highlights <span className="text-slate-400 font-normal">(optional)</span></h4>
        <p className="text-xs text-slate-400 mb-3">These render as the highlights grid on the project page. Unfilled fields are hidden on the site.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {highlightsFor(data.propertyType).map((key) => {
            const f = HL_FIELDS[key];
            if (!f) return null;
            return (
              <div key={key}>
                <label className={labelCls}>{f.label}</label>
                {f.type === "select" ? (
                  <SearchSelect value={data[key] || ""} onChange={(v) => set(key, v)} options={f.opts.map((o) => ({ id: o, name: o }))} placeholder="Select…" />
                ) : (
                  <input value={data[key] || ""} onChange={(e) => set(key, e.target.value)} placeholder={f.ph} className={fieldCls + (key === "totalUnits" ? " tabular-nums" : "")} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
